import { useEffect, useState } from 'react';
import VolunteerTable from './VolunteerTable';
import './astyles/volunteerTable.css';
import { exportToCSV, exportToPDF, formatDataForExport } from '../../utils/exportUtils';
import { Download, FileText, Filter } from 'lucide-react'; 

interface Volunteer {
  id: number;
  name?: string;
  firstname?: string;
  surname?: string;
  email: string;
  phone?: string;
  location?: string;
  skills?: string[] | null;
  availability?: string;
  application_status?: string;
  created_at?: string;
  status_updated_at?: string;
}

function VolunteerList() {
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState<Volunteer[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchVolunteers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';
        const response = await fetch(`${API_BASE_URL}/api/volunteers`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'user-id': '1' // Admin user ID
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status} - ${response.statusText}`);
        }
        
        const data = await response.json();
        
        // Ensure we have an array and process the data safely
        const processedData = Array.isArray(data) ? data.map(volunteer => ({
          ...volunteer,
          skills: volunteer.skills || [] // Ensure skills is always an array
        })) : [];
        
        setVolunteers(processedData);
        setFilteredVolunteers(processedData);
      } catch (err) {
        console.error('Error fetching volunteers:', err);
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('An unknown error occurred while fetching volunteers');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchVolunteers();
  }, []);

  // Filter volunteers based on search term and status
  useEffect(() => {
    let filtered = volunteers;

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(volunteer => {
        const name = `${volunteer.firstname || ''} ${volunteer.surname || ''}`.toLowerCase();
        const email = volunteer.email?.toLowerCase() || '';
        
        return name.includes(term) || email.includes(term);
      });
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(volunteer => 
        (volunteer.application_status || 'pending') === statusFilter
      );
    }

    setFilteredVolunteers(filtered);
  }, [searchTerm, statusFilter, volunteers]);

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    // Re-run the effect
    window.location.reload();
  };

  // Export functions
  const handleExportCSV = () => {
    const formattedData = formatDataForExport(filteredVolunteers, 'volunteers');
    exportToCSV(formattedData, `volunteers_${new Date().toISOString().split('T')[0]}`);
  };

  const handleExportPDF = async () => {
    setExportLoading(true);
    try {
      const formattedData = formatDataForExport(filteredVolunteers, 'volunteers');
      await exportToPDF(formattedData, `volunteers_${new Date().toISOString().split('T')[0]}`, 'Volunteer List');
    } finally {
      setExportLoading(false);
    }
  };

  // Get unique statuses for filter dropdown
  const getUniqueStatuses = () => {
    const statuses = volunteers.map(v => v.application_status || 'pending');
    return Array.from(new Set(statuses)).sort();
  };

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading volunteers...</p>
        <div className="loading-spinner"></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="error-container">
        <h3>Error Loading Volunteers</h3>
        <p>Failed to load volunteer data: {error}</p>
        <button onClick={handleRetry} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }
  return (
    <div className="volunteer-list-container">
      <div className="volunteer-header">
        <h2>Volunteer Management</h2>
        <div className="volunteer-controls">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
          
          <div className="filter-container">
            <Filter size={16} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="all">All Statuses</option>
              {getUniqueStatuses().map(status => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="export-buttons">
            <button
              onClick={handleExportCSV}
              className="export-btn csv-btn"
              disabled={filteredVolunteers.length === 0}
            >
              <Download size={16} />
              Export CSV
            </button>
            <button
              onClick={handleExportPDF}
              className="export-btn pdf-btn"
              disabled={filteredVolunteers.length === 0 || exportLoading}
            >
              <FileText size={16} />
              {exportLoading ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>
      </div>
      <VolunteerTable volunteers={filteredVolunteers} />
    </div>
  );
}
export default VolunteerList;